import { api } from './apiClient';

export const dimensionsService = {
  // Get transaction entities
  async getEntities(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/entities', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get products
  async getProducts(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/products', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get channels
  async getChannels(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/channels', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get businesses
  async getBusinesses(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/businesses', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get pricing schemes
  async getPricingSchemes(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/schemes', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get currencies
  async getCurrencies(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/currencies', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get acquirers
  async getAcquirers(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/acquirers', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get payout connectors
  async getPayoutConnectors(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/payout-connectors', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get payout rails
  async getPayoutRails(active = true) {
    try {
      const params = active !== 'all' ? { active } : {};
      const response = await api.get('/api/dimensions/payout-rails', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all dimensions at once (for forms) - optimized single API call
  async getAllDimensions() {
    try {
      const response = await api.get('/api/dimensions/all');
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Cache for dimensions (to avoid repeated API calls)
  _cache: new Map(),
  _cacheTimeout: 5 * 60 * 1000, // 5 minutes

  // Get cached dimensions
  async getCachedDimensions(type, active = true) {
    const cacheKey = `${type}_${active}`;
    const cached = this._cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this._cacheTimeout) {
      return cached.data;
    }

    let data;
    switch (type) {
      case 'entities':
        data = await this.getEntities(active);
        break;
      case 'products':
        data = await this.getProducts(active);
        break;
      case 'channels':
        data = await this.getChannels(active);
        break;
      case 'businesses':
        data = await this.getBusinesses(active);
        break;
      case 'schemes':
        data = await this.getPricingSchemes(active);
        break;
      case 'currencies':
        data = await this.getCurrencies(active);
        break;
      case 'acquirers':
        data = await this.getAcquirers(active);
        break;
      case 'payoutConnectors':
        data = await this.getPayoutConnectors(active);
        break;
      case 'payoutRails':
        data = await this.getPayoutRails(active);
        break;
      default:
        throw new Error(`Unknown dimension type: ${type}`);
    }

    this._cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    return data;
  },

  // Clear cache
  clearCache() {
    this._cache.clear();
  },

  // Format options for Select components
  formatForSelect(items, valueKey = 'id', labelKey = 'name') {
    return items.map(item => ({
      value: item[valueKey],
      label: item[labelKey],
      ...item,
    }));
  },

  // Get entity by ID
  async getEntityById(id) {
    try {
      const entities = await this.getCachedDimensions('entities');
      return entities.find(entity => entity.id === id);
    } catch (error) {
      throw error;
    }
  },

  // Get product by ID
  async getProductById(id) {
    try {
      const products = await this.getCachedDimensions('products');
      return products.find(product => product.id === id);
    } catch (error) {
      throw error;
    }
  },

  // Get channel by ID
  async getChannelById(id) {
    try {
      const channels = await this.getCachedDimensions('channels');
      return channels.find(channel => channel.id === id);
    } catch (error) {
      throw error;
    }
  },

  // Get currency by code
  async getCurrencyByCode(code) {
    try {
      const currencies = await this.getCachedDimensions('currencies');
      return currencies.find(currency => currency.code === code);
    } catch (error) {
      throw error;
    }
  },

  // Validate dimension references
  async validateReferences(ruleData) {
    const errors = [];

    try {
      // Validate entity
      if (ruleData.entityId) {
        const entity = await this.getEntityById(ruleData.entityId);
        if (!entity) {
          errors.push('Invalid entity selected');
        }
      }

      // Validate product
      if (ruleData.productId) {
        const product = await this.getProductById(ruleData.productId);
        if (!product) {
          errors.push('Invalid product selected');
        }
      }

      // Validate channel
      if (ruleData.channelId) {
        const channel = await this.getChannelById(ruleData.channelId);
        if (!channel) {
          errors.push('Invalid channel selected');
        }
      }

      // Validate currency
      if (ruleData.feeCurrencyCode) {
        const currency = await this.getCurrencyByCode(ruleData.feeCurrencyCode);
        if (!currency) {
          errors.push('Invalid currency selected');
        }
      }
    } catch (error) {
      errors.push('Failed to validate dimension references');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};
